import React, { useEffect, lazy, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useReportChecklistPublicQuery } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.hooks';
import ReportChecklistBoshHeader from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistBoshHeader';
import ReportChecklistBoshDescription from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistDescription';
import ReportChecklistDamage from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistDamage';
import ReportChecklistHeader from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistHeader';
import ReportChecklistAccessories from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistAccessories';
import ReportChecklistFooter from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistFooter';
import ReportChecklistRequestServices from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistRequestServices';
import ReportChecklistQuickChecklist from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistQuickChecklist';
import ReportChecklistComplementItems from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistComplementItems';
import ReportChecklistBoschAccessories from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistBoschAccessories';
import './checklist.less';
import ReportChecklistImages from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistImages';
import { isAppPortocar } from '@/index';
import ReportChecklistPortoComplementItems from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistPortoComplementItems';
import ReportChecklistObservations from '@/components/Reports/ReportPublic/ReportChecklist/ReportChecklistObservations';

const TemplateReport = lazy(
  () => import('@/components/_common/TemplateReport/TemplateReport'),
);

type SearchParams = { token: string };

const CustomerChecklistReport: React.FC = () => {
  const match = useRouteMatch();
  const { search } = useLocation();

  const params = Object.fromEntries(
    new URLSearchParams(search),
  ) as SearchParams;

  const [headerLoaded, setHeaderLoaded] = useState(false);
  const [damageLoaded, setDamageLoaded] = useState(false);

  const reportChecklist = useReportChecklistPublicQuery(params.token);

  const reportData = reportChecklist.data;
  const checklistReport = reportData?.checklistReportResponse;
  const company = checklistReport?.company;
  const companyReportConfig = reportData?.companyReportConfig;
  const signature = reportData?.signature;
  const accessories = checklistReport?.accessories;

  const hasToken = !!params.token;
  const isBosch = company?.bosch;

  useEffect(() => {
    if (
      headerLoaded &&
      damageLoaded &&
      !!company &&
      !reportChecklist.isLoading
    ) {
      document.title = getTitle(company);
    }
  }, [headerLoaded, damageLoaded, reportChecklist.data, signature]);

  function getTitle(data: typeof company) {
    return `Checklist_${data?.bosch ? 'Bosch' : ''}_${data?.name
      .split(' ')
      .join('-')}`;
  }

  return (
    <React.Suspense fallback={<>Carregando documento...</>}>
      {reportChecklist.isLoading && (
        <div className="modal-loading-overlay">
          <div style={{ textAlign: 'center' }}>
            <h2>
              <strong>Carregando dados...</strong>
            </h2>
            <p>Carregando os dados do checklist do veículo.</p>
          </div>
        </div>
      )}

      {(!hasToken || (!reportChecklist.data && !reportChecklist.isLoading)) && (
        <div className="modal-loading-overlay">
          <div style={{ textAlign: 'center' }}>
            <h2>
              <strong>Não encontramos informações de consulta</strong>
            </h2>

            <p>
              Ainda não foi liberado dados de checklist para os parâmetros
              informados
            </p>
          </div>
        </div>
      )}

      {match.isExact &&
        !reportChecklist.isLoading &&
        !!reportChecklist.data &&
        !!companyReportConfig && (
          <TemplateReport>
            <main className="reportChecklist">
              {!company && (
                <div className="modal-loading-overlay">
                  <div style={{ textAlign: 'center' }}>
                    <h2>
                      <strong>
                        O checklist selecionado não há informações.
                      </strong>
                    </h2>
                    <p>
                      Solicite a oficina para finalizar o processo de checklist
                    </p>
                  </div>
                </div>
              )}

              {isBosch ? (
                <ReportChecklistBoshHeader
                  checklist={reportData?.checklistReportResponse}
                  setLoaded={setHeaderLoaded}
                  companyConfig={{
                    ...companyReportConfig,
                    id: String(companyReportConfig?.id),
                  }}
                />
              ) : (
                <ReportChecklistHeader
                  company={company}
                  setLoaded={setHeaderLoaded}
                  companyConfig={{
                    ...companyReportConfig,
                    id: String(companyReportConfig?.id),
                  }}
                />
              )}

              <ReportChecklistBoshDescription
                checklist={reportData?.checklistReportResponse}
              />

              <ReportChecklistDamage
                checklist={reportData?.checklistReportResponse}
                setLoaded={setDamageLoaded}
              />

              {isBosch && (
                <ReportChecklistBoschAccessories
                  checklist={reportData?.checklistReportResponse}
                />
              )}

              {!isBosch && (
                <ReportChecklistAccessories
                  accessories={accessories}
                  includeAccessoryColumns={false}
                />
              )}

              <ReportChecklistRequestServices
                checklist={reportData?.checklistReportResponse}
              />

              <ReportChecklistQuickChecklist
                checklist={reportData?.checklistReportResponse}
              />

              {isAppPortocar ? (
                <ReportChecklistPortoComplementItems
                  checklist={reportData?.checklistReportResponse}
                />
              ) : (
                <ReportChecklistComplementItems
                  checklist={reportData?.checklistReportResponse}
                />
              )}

              <ReportChecklistImages
                checklist={reportData?.checklistReportResponse}
              />

              <ReportChecklistObservations
                checklist={reportData?.checklistReportResponse}
              />

              <ReportChecklistFooter src={signature?.signature} isPublic />
            </main>
          </TemplateReport>
        )}
    </React.Suspense>
  );
};

export default CustomerChecklistReport;
